<template>
  <article :class="cardLayoutClasses" :data-product-id="product.id">
    <section
      class="relative border rounded-md bg-white w-full overflow-hidden rounded-lg transition-all"
      :class="[
        inCart ? productCardInCartShadowClasses : cardShadowClasses,
        isHighlighted && 'ring-2 ring-red-500 ring-offset-2 animate-pulse'
      ]"
    >
      <div class="mx-3 my-2 flex">
        <article class="flex-grow" @click="collapsed = !collapsed">
          <span
            v-for="label in product.labels"
            :key="label"
            class="inline-block text-xxs uppercase bg-brand-100 text-brand-900 px-1 py-1 mr-1 rounded-sm shadow-sm font-manrope tracking-widest"
          >
            {{ label }}
          </span>
          <h2 class="pr-3 pt-2 sm:pt-2 md:pt-3 lg:pt-4 pb-2 md:pb-3 font-bold text-lg leading-5 tracking-tighter">
            {{ product.name }}
          </h2>
          <p
            v-if="product.description"
            :class="['pb-3 pr-3 text-gray-brand-700 text-sm whitespace-pre-line', collapsed && 'collapsed']"
          >
            {{ product.description }}
          </p>
        </article>
        <div v-if="product.attachments.length" class="flex-shrink-0 ml-2">
          <img
            :src="product.attachments[0].url"
            :alt="product.name"
            class="w-20 h-20 object-cover rounded-lg cursor-pointer"
            data-testid="v2-product-card-legacy-thumb"
            @click="isHeroExpanded = !isHeroExpanded"
          />
        </div>
      </div>

      <div v-if="isHeroExpanded && heroUrls.length" class="px-3 pb-3">
        <ProductPhotoCarousel
          :urls="heroUrls"
          :alt="product.name"
          photo-class="w-full rounded-lg cursor-pointer"
          auto-height
          test-id-prefix="v2-product-card-legacy-hero"
          @photo-click="isHeroExpanded = false"
        />
      </div>

      <ProductNutritionalInformation v-if="nutritionalInformation" :nutritional-information="nutritionalInformation" />

      <V2ProductPricing
        :product="product"
        :submittable-cart-item="submittableCartItem"
        :v2-cart-item="v2CartItem"
        :pricing-tiers="pricingTiers"
        :is-upsell="isUpsell"
      />

      <V2ProductVariants
        v-if="product.variants.length > 0"
        :variants="product.variants"
        :product="product"
        :submittable-cart-item="submittableCartItem"
        :show-price="product.show_prices !== false"
        :pricing-tiers="pricingTiers"
      />

      <div v-if="offerToCustomizeAnother" class="pt-4 pb-4 px-6 text-sm text-right">
        <button
          class="inline-flex items-center px-3 py-2 rounded-full border border-brand-800 hover:bg-brand-100 text-brand-800"
          @click="customizeAnother"
        >
          <div class="inline-block font-semibold text-xs">
            {{ drawerEnabled ? 'Add to cart' : 'Customize another' }}
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="ml-1 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </section>
  </article>
</template>

<script>
import { inject, ref } from 'vue'
import V2ProductPricing from '@/components/v2/V2ProductPricing'
import V2ProductVariants from '@/components/v2/V2ProductVariants'
import ProductNutritionalInformation from '@/components/Product/ProductNutritionalInformation'
import ProductPhotoCarousel from '@/components/Product/ProductPhotoCarousel.vue'
import { cardShadowClasses, productCardInCartShadowClasses } from '@/config'
import { useProductCardLayout } from '@/composables/useProductCardLayout'
import { fromV2, cartItemVariantOptionsToSelection, combinedNutrition } from '@/models/productViewModel'

export default {
  name: 'V2ProductCardLegacy',

  components: {
    V2ProductPricing,
    V2ProductVariants,
    ProductNutritionalInformation,
    ProductPhotoCarousel
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    submittableCartItem: {
      type: Object,
      default: null
    },
    v2CartItem: {
      type: Object,
      default: null
    },
    pricingTiers: {
      type: Array,
      default: () => []
    },
    isUpsell: {
      type: Boolean,
      default: false
    }
  },

  emits: ['add-to-cart'],

  data() {
    return {
      collapsed: true,
      isHeroExpanded: false
    }
  },

  computed: {
    inCart() {
      return (this.submittableCartItem?.quantity || 0) > 0
    },
    drawerEnabled() {
      return this.$store.getters.hasCartDrawer
    },
    offerToCustomizeAnother() {
      if (this.hideCustomizeAnother) return false
      return this.product.variants.length > 0 && this.inCart
    },
    isHighlighted() {
      return this.v2HighlightedProductId === this.product.id
    },
    heroUrls() {
      return this.product.attachments.map((attachment) => attachment.high_res_url).filter(Boolean)
    },
    nutritionalInformation() {
      return combinedNutrition(
        fromV2({ product: this.product }),
        cartItemVariantOptionsToSelection(this.submittableCartItem?.cart_item_variant_options_attributes)
      )
    }
  },

  setup() {
    const v2AddCustomization = inject('v2AddCustomization', null)
    const v2HighlightedProductId = inject('v2HighlightedProductId', ref(null))
    const hideCustomizeAnother = inject('v2HideCustomizeAnother', ref(false))
    const { cardLayoutClasses } = useProductCardLayout()
    return {
      cardLayoutClasses,
      cardShadowClasses,
      productCardInCartShadowClasses,
      v2AddCustomization,
      v2HighlightedProductId,
      hideCustomizeAnother
    }
  },

  methods: {
    customizeAnother() {
      if (this.drawerEnabled) {
        this.$emit('add-to-cart', this.submittableCartItem?.frontend_key)
        return
      }
      if (this.v2AddCustomization) {
        const isUpsell = this.submittableCartItem?.frontend_key?.includes('-upsell') || false
        this.v2AddCustomization(this.product.cpsp_id, { isUpsell })
      }
    }
  }
}
</script>

<style scoped>
.collapsed {
  height: 3em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 0.5rem;
}
</style>
